import axios from 'axios';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Confetti from 'react-confetti';
import {
  FaBan,
  FaEdit,
  FaEllipsisH,
  FaRegThumbsDown,
  FaRegThumbsUp,
  FaStopwatch,
  FaToggleOff,
  FaToggleOn,
  FaUndo,
  FaVolumeMute,
  FaVolumeUp,
} from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import PassageWithTranslation from '../../components/passageWithTranslation/PassageWithTranslation';
import PlayButton from '../../components/playButton/PlayButton';
import { useUser } from '../../context/UserContext';
import { CardData, CardField } from '../../interfaces/cards';
import EditCardPopup from '../../popups/editCard/EditCardPopup';
import { getPhoneticName, isPhoneticsSupported } from '../../utils/formatting';
import './ReviewPage.css';

const MAX_CARDS_PER_REQUEST = 10;

const ReviewPage: React.FC = () => {
  const { userData, refreshUser } = useUser();
  const [deckId, setDeckId] = useState<number>();
  const [cards, setCards] = useState<CardData[]>([]);
  const [totalCards, setTotalCards] = useState<number>(0);
  const [currentCardIndex, setCurrentCardIndex] = useState<number>(0);
  const [hasMoreCards, setHasMoreCards] = useState<boolean>(true);
  const [showAnswer, setShowAnswer] = useState<boolean>(false);
  const [disableButtons, setDisableButtons] = useState<boolean>(false);
  const [passProgress, setPassProgress] = useState<number>(0);
  const [failProgress, setFailProgress] = useState<number>(0);
  const [audioKey, setAudioKey] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [editingCard, setEditingCard] = useState<CardData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [initialLoadComplete, setInitialLoadComplete] =
    useState<boolean>(false);
  const [showPhonetics, setShowPhonetics] = useState<boolean>(() => {
    const saved = localStorage.getItem('showPhonetics');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [autoPlayEnabled, setAutoPlayEnabled] = useState<boolean>(() => {
    const saved = localStorage.getItem('autoPlayEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [showTimer, setShowTimer] = useState<boolean>(() => {
    const saved = localStorage.getItem('showTimer');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [longestStreak, setLongestStreak] = useState<number>(0);
  const [currentStreak, setCurrentStreak] = useState<number>(0);
  const [buttonTimeout, setButtonTimeout] = useState<boolean>(false);

  // Total session timer (for statistics)
  const [sessionStartTime] = useState<number>(Date.now());
  const [totalInactiveTime, setTotalInactiveTime] = useState<number>(0);
  const [lastInactiveStart, setLastInactiveStart] = useState<number | null>(
    null
  );
  const [finalSessionTime, setFinalSessionTime] = useState<number | null>(null);

  // Per-card timer (for UI display)
  const [currentCardTime, setCurrentCardTime] = useState<number>(0);
  const cardTimerIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate total active session time (for statistics)
  const getActiveSessionTime = useCallback(() => {
    // If we have a final session time, use that
    if (finalSessionTime !== null) {
      return finalSessionTime;
    }

    // Current total time
    const totalTime = Date.now() - sessionStartTime;

    // Current inactive time (including ongoing inactive period if window is blurred)
    let currentInactiveTime = totalInactiveTime;
    if (lastInactiveStart !== null) {
      currentInactiveTime += Date.now() - lastInactiveStart;
    }

    // Return active time only
    return totalTime - currentInactiveTime;
  }, [
    finalSessionTime,
    totalInactiveTime,
    lastInactiveStart,
    sessionStartTime,
  ]);

  // Track window focus/visibility changes for both timers
  useEffect(() => {
    // Don't track visibility if review is complete
    if (currentCardIndex >= totalCards && totalCards > 0) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Window lost focus - start tracking inactive time
        setLastInactiveStart(Date.now());

        // Pause the card timer by clearing the interval
        if (cardTimerIntervalRef.current) {
          clearInterval(cardTimerIntervalRef.current);
          cardTimerIntervalRef.current = null;
        }
      } else if (lastInactiveStart !== null) {
        // Window regained focus - calculate and add to total inactive time
        const inactiveTime = Date.now() - lastInactiveStart;
        setTotalInactiveTime((prevTotal) => prevTotal + inactiveTime);
        setLastInactiveStart(null);

        // Resume the card timer if not showing answer
        if (!showAnswer) {
          // Start a new interval that continues from where we left off
          const interval = setInterval(() => {
            setCurrentCardTime((prevTime) => prevTime + 100);
          }, 100);
          cardTimerIntervalRef.current = interval;
        }
      }
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [lastInactiveStart, showAnswer, totalCards, currentCardIndex]);

  // Capture final session time when review is complete
  useEffect(() => {
    if (
      currentCardIndex >= totalCards &&
      totalCards > 0 &&
      finalSessionTime === null
    ) {
      setFinalSessionTime(getActiveSessionTime());

      // Clear any existing card timer
      if (cardTimerIntervalRef.current) {
        clearInterval(cardTimerIntervalRef.current);
        cardTimerIntervalRef.current = null;
      }
    }
  }, [currentCardIndex, totalCards, finalSessionTime, getActiveSessionTime]);

  const fetchReviewData = useCallback(
    async (offset: number) => {
      try {
        const endpoint = '/api/review';
        const cards_remaining = totalCards - currentCardIndex;
        console.log('totalCards', totalCards);
        console.log('currentCardIndex', currentCardIndex);
        console.log('cards_remaining', cards_remaining);
        const params = new URLSearchParams({
          limit: Math.min(MAX_CARDS_PER_REQUEST, cards_remaining).toString(),
          offset: offset.toString(),
        });

        if (deckId) {
          params.append('deck_id', deckId.toString());
        }

        const response = await axios.get(`${endpoint}?${params}`);
        const data = response.data;

        // This function is now only used for pagination (offset > 0)
        // Initial load is handled separately in the useEffect
        const newCards = data.cards.filter(
          (newCard: CardData) =>
            !cards.some(
              (existingCard: CardData) => existingCard.id === newCard.id
            )
        );
        setCards((prevCards) => [...prevCards, ...newCards]);
        setHasMoreCards(newCards.length + cards.length < totalCards);
      } catch (error) {
        console.error('Error fetching review data:', error);
      }
    },
    [deckId, totalCards, cards, currentCardIndex]
  );

  // Check if we need to load more cards
  const checkAndLoadMoreCards = useCallback(async () => {
    const remainingCards = cards.length - currentCardIndex;

    // Load more cards if we have less than 3 remaining and there are more to load
    if (remainingCards < 3 && hasMoreCards) {
      await fetchReviewData(0);
    }
  }, [cards.length, currentCardIndex, fetchReviewData, hasMoreCards]);

  useEffect(() => {
    const state = location.state as { deckId?: number };
    if (location.state && state.deckId) {
      setDeckId(state.deckId);
    }
    // Trigger initial load after deck ID is set (or if no deck ID)
    if (!initialLoadComplete) {
      const loadInitialData = async () => {
        try {
          const targetDeckId = state?.deckId;
          const endpoint = '/api/review';
          const params = new URLSearchParams({
            limit: MAX_CARDS_PER_REQUEST.toString(),
            offset: '0',
          });

          if (targetDeckId) {
            params.append('deck_id', targetDeckId.toString());
          }

          const response = await axios.get(`${endpoint}?${params}`);
          const data = response.data;

          setHasMoreCards(data.cards.length < data.total);
          setCards(data.cards || []);
          setTotalCards(data.total || 0);
          setInitialLoadComplete(true);
        } catch (error) {
          console.error('Error fetching initial review data:', error);
        } finally {
          setIsLoading(false);
        }
      };

      loadInitialData();
    }
  }, [location.state, initialLoadComplete]);

  // Check for more cards whenever currentCardIndex changes (but only after initial load)
  useEffect(() => {
    if (initialLoadComplete) {
      checkAndLoadMoreCards();
    }
  }, [currentCardIndex, checkAndLoadMoreCards, initialLoadComplete]);

  // Start a new card timer whenever we move to a new card
  useEffect(() => {
    // Reset timer only on new card, not when showAnswer changes
    if (!showAnswer) {
      // Clear any existing interval
      if (cardTimerIntervalRef.current) {
        clearInterval(cardTimerIntervalRef.current);
        cardTimerIntervalRef.current = null;
      }

      // Only start new timer if window is focused
      if (!document.hidden) {
        const interval = setInterval(() => {
          setCurrentCardTime((prevTime) => prevTime + 100);
        }, 100);

        cardTimerIntervalRef.current = interval;
      }
    }

    return () => {
      if (cardTimerIntervalRef.current) {
        clearInterval(cardTimerIntervalRef.current);
        cardTimerIntervalRef.current = null;
      }
    };
  }, [currentCardIndex, showAnswer]);

  const handleShowAnswer = () => {
    // Stop the card timer when answer is shown
    if (cardTimerIntervalRef.current) {
      clearInterval(cardTimerIntervalRef.current);
      cardTimerIntervalRef.current = null;
    }

    setButtonTimeout(true);
    setShowAnswer(true);
    setAudioKey((prevKey) => prevKey + 1);

    setTimeout(() => {
      setButtonTimeout(false);
    }, 500);
  };

  const handleNextCard = () => {
    setShowAnswer(false);
    setCurrentCardIndex(currentCardIndex + 1);
    // Reset the timer when moving to next card
    setCurrentCardTime(0);
  };

  const updateStreaks = (isPass: boolean) => {
    if (isPass) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      setLongestStreak(Math.max(longestStreak, newStreak));
    } else {
      setCurrentStreak(0);
    }
  };

  const handleCardResult = async (isPass: boolean) => {
    setDisableButtons(true);
    setButtonTimeout(true);

    try {
      const cardId = cards[currentCardIndex].id;
      const endpoint = isPass ? 'pass' : 'fail';
      await axios.put(`/api/cards/${cardId}/${endpoint}`);

      if (currentCardIndex === 0) {
        refreshUser();
      }

      if (isPass) {
        setPassProgress(passProgress + 1);
      } else {
        setFailProgress(failProgress + 1);
      }

      setCards((prevCards) => {
        const updatedCards = [...prevCards];
        updatedCards[currentCardIndex] = {
          ...updatedCards[currentCardIndex],
          pass: isPass,
        };
        return updatedCards;
      });

      handleNextCard();
      updateStreaks(isPass);

      // Add a timeout before enabling buttons again
      setTimeout(() => {
        setButtonTimeout(false);
      }, 500); // 500ms timeout to prevent accidental clicks
    } catch (error) {
      console.error(
        `Error marking card as ${isPass ? 'passed' : 'failed'}:`,
        error
      );
      setButtonTimeout(false);
    } finally {
      setDisableButtons(false);
    }
  };

  const handlePass = () => handleCardResult(true);
  const handleFail = () => handleCardResult(false);

  const handleDisable = async () => {
    setDisableButtons(true);
    setButtonTimeout(true);
    setShowMenu(false);
    try {
      await axios.patch('/api/cards/disable', {
        card_ids: [cards[currentCardIndex].id],
        disabled: true,
      });
      setPassProgress((prev) => prev + 1);
      setCards((prevCards) => {
        const updated = [...prevCards];
        updated[currentCardIndex] = { ...updated[currentCardIndex], pass: true };
        return updated;
      });
      handleNextCard();
      setTimeout(() => setButtonTimeout(false), 500);
    } catch (error) {
      console.error('Error disabling card:', error);
      setButtonTimeout(false);
    } finally {
      setDisableButtons(false);
    }
  };

  const handleUndo = async () => {
    setDisableButtons(true);
    try {
      const card = cards[currentCardIndex - 1];
      const cardState = {
        id: card.id,
        interval: card.interval,
        repetitions: card.repetitions,
        ease_factor: card.ease_factor,
        next_review_at: card.next_review_at,
        last_seen: card.last_seen,
        is_new: card.is_new,
        is_review: card.is_review,
      };
      await axios.put(`/api/cards/${card.id}/undo`, cardState);

      if (card.pass) {
        setPassProgress(passProgress - 1);
      } else {
        setFailProgress(failProgress - 1);
      }

      setShowAnswer(false);
      setCurrentCardIndex((prevIndex) => prevIndex - 1);
    } catch (error) {
      console.error('Error marking card as failed:', error);
    } finally {
      setDisableButtons(false);
    }
  };

  const handleEditCard = () => {
    if (cards[currentCardIndex]) {
      setEditingCard(cards[currentCardIndex]);
    }
  };

  const handleCloseEditPopup = () => {
    setEditingCard(null);
  };

  const handleSubmitEdit = async () => {
    if (editingCard) {
      try {
        // Fetch the updated card data
        const response = await axios.get(`/api/cards/${editingCard.id}`);
        const fetchedCard = response.data;

        // Update only specific fields
        const updatedCardData = {
          native_language: fetchedCard.native_language,
          target_language: fetchedCard.target_language,
          target_language_list: fetchedCard.target_language_list,
          phonetic: fetchedCard.phonetic,
          phonetic_list: fetchedCard.phonetic_list,
          translation_list: fetchedCard.translation_list,
          formality: fetchedCard.formality,
        };

        // Update all cards with the same note_id
        setCards((prevCards) => {
          return prevCards.map((card) => {
            if (card.note_id === editingCard.note_id) {
              console.log('Updating card:', card.note_id);
              return {
                ...card,
                ...updatedCardData,
              };
            }
            return card;
          });
        });

        setEditingCard(null);
      } catch (error) {
        console.error('Error fetching updated card data:', error);
      }
    }
  };

  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const togglePhonetics = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = !showPhonetics;
    setShowPhonetics(newValue);
    localStorage.setItem('showPhonetics', JSON.stringify(newValue));
  };

  const toggleAutoPlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = !autoPlayEnabled;
    setAutoPlayEnabled(newValue);
    localStorage.setItem('autoPlayEnabled', JSON.stringify(newValue));
  };

  const toggleTimer = (e: React.MouseEvent) => {
    e.stopPropagation();
    const newValue = !showTimer;
    setShowTimer(newValue);
    localStorage.setItem('showTimer', JSON.stringify(newValue));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Stop the card timer when showing the answer
  useEffect(() => {
    if (showAnswer && cardTimerIntervalRef.current) {
      clearInterval(cardTimerIntervalRef.current);
      cardTimerIntervalRef.current = null;
    }
  }, [showAnswer]);

  // Format the time for display
  const formatCardTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);

    // If less than a minute, just show seconds
    if (seconds < 60) {
      return `${seconds}`;
    }

    // Otherwise show minutes:seconds format
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Format for minutes:seconds display for stats
  const formatTimeStats = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const renderLayout = (
    layout: string[][],
    fields: CardField[],
    cardData: CardData
  ) => {
    return layout.map((row, rowIndex) => (
      <div key={rowIndex} className="layout-row">
        {row.map((fieldKey, fieldIndex) => {
          const fieldConfig = fields.find((field) => field.field === fieldKey);
          if (fieldConfig) {
            const value = cardData[fieldConfig.field as keyof CardData];
            if (typeof value === 'string' || typeof value === 'number') {
              if (fieldConfig.field === 'audio') {
                if (cardData.audio) {
                  return (
                    <PlayButton
                      key={`${fieldIndex}-${audioKey}`} // Add audioKey to force re-render
                      disabled={disableButtons}
                      src={cardData.audio}
                      isLoading={false}
                      autoPlay={autoPlayEnabled} // Set autoPlay to true when answer is shown
                    />
                  );
                }
              } else if (fieldConfig.field === 'formality') {
                if (cardData.formality) {
                  return (
                    <div
                      key={`${fieldIndex}-${audioKey}`} // Add audioKey to force re-render
                      className="card-field"
                      style={fieldConfig.style}
                    >
                      ({cardData.formality})
                    </div>
                  );
                }
              } else if (fieldConfig.field === 'target_language') {
                return (
                  <PassageWithTranslation
                    key={fieldIndex}
                    original={cardData.target_language_list}
                    translations={cardData.translation_list}
                    phonetics={cardData.phonetic_list}
                    savedWords={[]} // You might want to implement this feature later
                    showPhonetics={showPhonetics} // Use the state variable here
                    languageCode={userData?.learning_language_code || ''}
                    align="center"
                    canShowTooltip={showAnswer}
                  />
                );
              } else {
                return (
                  <div
                    key={fieldIndex}
                    className="card-field"
                    style={fieldConfig.style}
                  >
                    {value}
                  </div>
                );
              }
            }
          }
          return null;
        })}
      </div>
    ));
  };

  const reviewComplete = currentCardIndex >= totalCards && totalCards > 0;

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="review-page">
      <div className="progress-bar">
        {passProgress !== 0 && (
          <div
            className="pass-progress"
            style={{ width: `${(passProgress / totalCards) * 100}%` }}
          />
        )}
        {failProgress !== 0 && (
          <div
            className="fail-progress"
            style={{ width: `${(failProgress / totalCards) * 100}%` }}
          />
        )}
        {totalCards - currentCardIndex > 0 && (
          <div
            className="uncomplete-progress"
            style={{
              width: `${((totalCards - (passProgress + failProgress)) / totalCards) * 100}%`,
            }}
          />
        )}
      </div>
      <div className="review-card-actions">
        <button
          className="phn-text-button"
          style={{
            width: '140px',
          }}
          disabled={currentCardIndex === totalCards || isLoading}
          onClick={handleEditCard}
        >
          Edit <FaEdit />
        </button>
        <span className="review-card-progress">
          {currentCardIndex >= totalCards
            ? `${totalCards} / ${totalCards}`
            : `${currentCardIndex + 1} / ${totalCards}`}
        </span>
        <button
          className="phn-text-button"
          style={{
            width: '140px',
          }}
          disabled={currentCardIndex <= 0 || isLoading}
          onClick={handleUndo}
        >
          Undo <FaUndo />
        </button>
      </div>
      <>
        {reviewComplete ? (
          <>
            {(passProgress / totalCards) * 100 > 50 && (
              <Confetti recycle={false} numberOfPieces={200} gravity={0.2} />
            )}
            <div className="review-complete-container">
              <h3>Review Complete!</h3>

              <div className="review-stats">
                <div className="stat-group">
                  <h3>Accuracy</h3>
                  <div className="review-percentage">
                    <div className="stat-item">
                      <span>Correct:</span>
                      <span className="stat-value success">
                        {Math.round((passProgress / totalCards) * 100)}%
                      </span>
                    </div>
                    <div className="stat-item">
                      <span>Incorrect:</span>
                      <span className="stat-value error">
                        {Math.round((failProgress / totalCards) * 100)}%
                      </span>
                    </div>
                  </div>
                </div>

                <div className="stat-group">
                  <h3>Performance</h3>
                  <div className="stat-item">
                    <span>Total Cards:</span>
                    <span className="stat-value">{totalCards}</span>
                  </div>
                  <div className="stat-item">
                    <span>Best Streak:</span>
                    <span className="stat-value">{longestStreak}</span>
                  </div>
                  <div className="stat-item">
                    <span>Time Spent:</span>
                    <span className="stat-value">
                      {formatTimeStats(getActiveSessionTime())}
                    </span>
                  </div>
                  <div className="stat-item">
                    <span>Avg. Time/Card:</span>
                    <span className="stat-value">
                      {Math.round(getActiveSessionTime() / totalCards / 1000)}s
                    </span>
                  </div>
                </div>
              </div>

              <div className="review-actions">
                <button
                  className="review-action-button"
                  onClick={() => navigate('/dashboard')}
                >
                  Return Home
                </button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="card-container">
              {cards.length > 0 && currentCardIndex < cards.length ? (
                <div className="phn-white-card card-display">
                  {showTimer && (
                    <div className="stopwatch-display">
                      <div className="stopwatch-icon">
                        <FaStopwatch
                          style={{
                            color:
                              currentCardTime >= 20000
                                ? 'red'
                                : currentCardTime >= 10000
                                  ? '#f0ad4e' // yellow/warning color
                                  : 'var(--primary-main)',
                          }}
                        />
                        <span
                          className="stopwatch-time"
                          style={{
                            fontSize: currentCardTime < 60000 ? '18px' : '12px',
                            color:
                              currentCardTime >= 20000
                                ? 'red'
                                : currentCardTime >= 10000
                                  ? '#f0ad4e' // yellow/warning color
                                  : 'var(--primary-main)',
                          }}
                        >
                          {formatCardTime(currentCardTime)}
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="menu-container" ref={menuRef}>
                    <button
                      className="menu-toggle"
                      onClick={toggleMenu}
                      title="Options"
                    >
                      <FaEllipsisH />
                    </button>
                    {showMenu && (
                      <div className="menu-dropdown">
                        {isPhoneticsSupported(
                          userData?.learning_language_code || ''
                        ) && (
                          <div className="menu-item">
                            <span>
                              {showPhonetics ? 'Hide' : 'Show'}{' '}
                              {getPhoneticName(
                                userData?.learning_language_code || ''
                              )}
                            </span>
                            <button
                              onClick={togglePhonetics}
                              className="icon-button"
                            >
                              {showPhonetics ? <FaToggleOn /> : <FaToggleOff />}
                            </button>
                          </div>
                        )}
                        <div className="menu-item">
                          <span>
                            {autoPlayEnabled ? 'Disable' : 'Enable'} Autoplay
                          </span>
                          <button
                            onClick={toggleAutoPlay}
                            className="icon-button"
                          >
                            {autoPlayEnabled ? (
                              <FaVolumeUp />
                            ) : (
                              <FaVolumeMute />
                            )}
                          </button>
                        </div>
                        <div className="menu-item">
                          <span>{showTimer ? 'Hide' : 'Show'} Timer</span>
                          <button onClick={toggleTimer} className="icon-button">
                            {showTimer ? <FaToggleOn /> : <FaToggleOff />}
                          </button>
                        </div>
                        <div className="menu-item">
                          <span>Disable Card</span>
                          <button
                            onClick={handleDisable}
                            className="icon-button"
                            disabled={disableButtons || buttonTimeout}
                            style={{ color: '#dc3545' }}
                          >
                            <FaBan />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="card-content">
                    <div className="card-front">
                      {renderLayout(
                        cards[currentCardIndex].front.layout,
                        cards[currentCardIndex].front.fields,
                        cards[currentCardIndex]
                      )}
                    </div>
                    {showAnswer && (
                      <div className="card-back">
                        {renderLayout(
                          cards[currentCardIndex].back.layout,
                          cards[currentCardIndex].back.fields,
                          cards[currentCardIndex]
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="loading-container">
                  <div className="loading-spinner" />
                </div>
              )}
            </div>
            <div className="button-container">
              {!showAnswer ? (
                <button
                  className="show-answer-button"
                  onClick={handleShowAnswer}
                  disabled={
                    disableButtons ||
                    buttonTimeout ||
                    currentCardIndex >= cards.length
                  }
                >
                  Show Answer
                </button>
              ) : (
                <>
                  <button
                    className="pass-button"
                    onClick={handlePass}
                    disabled={disableButtons || buttonTimeout}
                  >
                    Pass
                    <FaRegThumbsUp />
                  </button>
                  <button
                    className="fail-button"
                    onClick={handleFail}
                    disabled={disableButtons || buttonTimeout}
                  >
                    Fail
                    <FaRegThumbsDown />
                  </button>
                </>
              )}
            </div>
          </>
        )}
      </>
      {editingCard && (
        <EditCardPopup
          card={editingCard}
          language_code={userData?.learning_language_code || ''}
          onClose={handleCloseEditPopup}
          onSubmit={handleSubmitEdit}
        />
      )}
    </div>
  );
};

export default ReviewPage;
